import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Types for financial data
export interface Transaction {
  id: string;
  merchant: string;
  amount: number;
  date: string;
  category: string;
}

export interface Bill {
  id: string;
  name: string;
  amount: number;
  dueDate: string;
  recurring: boolean;
  frequency?: 'monthly' | 'quarterly' | 'yearly';
  paid: boolean;
}

export interface Asset {
  id: string;
  type: 'cash' | 'investments' | 'crypto' | 'realEstate' | 'other';
  name: string;
  value: number;
  description?: string;
}

export interface Income {
  id: string;
  source: string;
  amount: number;
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'yearly';
  nextPayment?: string;
}

export interface SavingsGoal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  targetDate?: string;
  category?: string;
}

export interface RiskProfile {
  riskTolerance: 'low' | 'medium' | 'high';
  investmentHorizon: 'short' | 'medium' | 'long';
  investmentGoals: string[];
  preferredInvestments: string[];
  financialSituation: {
    emergencyFund: boolean;
    debtLevel: 'low' | 'medium' | 'high';
    incomeStability: 'low' | 'medium' | 'high';
  };
}

// Main user data interface
export interface UserData {
  profile: {
    name: string;
    email: string;
    avatar?: string;
  };
  finances: {
    income: {
      monthly: number;
      sources: Income[];
    };
    spending: {
      monthly: number;
      categories: {
        [key: string]: number;
      };
    };
    transactions: Transaction[];
    bills: Bill[];
    assets: {
      total: number;
      breakdown: {
        cash: number;
        investments: number;
        crypto: number;
        realEstate: number;
        other: number;
      };
      items: Asset[];
    };
    savingsGoals: SavingsGoal[];
    investmentPerformance: {
      month: string;
      value: number;
    }[];
  };
  aiInsights: {
    riskProfile: RiskProfile;
    recommendations: {
      id: string;
      category: string;
      title: string;
      description: string;
      potentialSavings?: number;
      priority: 'low' | 'medium' | 'high';
      implementationStatus: 'pending' | 'in_progress' | 'completed';
    }[];
    spendingInsights: {
      id: string;
      category: string;
      description: string;
      comparison: {
        current: number;
        previous: number;
        percentChange: number;
      };
    }[];
    fraudAlerts: {
      id: string;
      date: string;
      description: string;
      severity: 'low' | 'medium' | 'high';
      status: 'new' | 'reviewing' | 'resolved';
    }[];
  };
}

// Initial default data
const defaultUserData: UserData = {
  profile: {
    name: "Bill",
    email: "bill@example.com",
  },
  finances: {
    income: {
      monthly: 4000,
      sources: [
        {
          id: "income-1",
          source: "Primary Job",
          amount: 4000,
          frequency: "monthly"
        }
      ]
    },
    spending: {
      monthly: 2650,
      categories: {
        housing: 1200,
        food: 500,
        transport: 300,
        entertainment: 350,
        utilities: 200,
        other: 100
      }
    },
    transactions: [
      { id: "tx-1", merchant: 'Amazon', amount: 49.99, date: '2023-06-20', category: 'Shopping' },
      { id: "tx-2", merchant: 'Starbucks', amount: 5.75, date: '2023-06-19', category: 'Food & Drink' },
      { id: "tx-3", merchant: 'Gas Station', amount: 45.00, date: '2023-06-18', category: 'Transportation' },
      { id: "tx-4", merchant: 'Grocery Store', amount: 112.43, date: '2023-06-16', category: 'Groceries' },
    ],
    bills: [
      { id: "bill-1", name: 'Rent', amount: 1200, dueDate: '2023-06-25', recurring: true, frequency: 'monthly', paid: false },
      { id: "bill-2", name: 'Car Insurance', amount: 150, dueDate: '2023-06-28', recurring: true, frequency: 'monthly', paid: false },
      { id: "bill-3", name: 'Internet', amount: 80, dueDate: '2023-07-01', recurring: true, frequency: 'monthly', paid: false },
    ],
    assets: {
      total: 12475.55,
      breakdown: {
        cash: 5000,
        investments: 7475.55,
        crypto: 1500,
        realEstate: 0,
        other: 0
      },
      items: [
        { id: "asset-1", type: "cash", name: "Checking Account", value: 2000, description: "Main checking account" },
        { id: "asset-2", type: "cash", name: "Savings Account", value: 3000, description: "Emergency fund" },
        { id: "asset-3", type: "investments", name: "Index Fund", value: 5475.55, description: "S&P 500 index fund" },
        { id: "asset-4", type: "investments", name: "Company Stock", value: 2000, description: "Employer stock options" },
        { id: "asset-5", type: "crypto", name: "Bitcoin", value: 1000, description: "BTC investment" },
        { id: "asset-6", type: "crypto", name: "Ethereum", value: 500, description: "ETH investment" },
      ]
    },
    savingsGoals: [
      {
        id: "goal-1",
        name: "Emergency Fund",
        targetAmount: 20000,
        currentAmount: 12400,
        category: "Safety"
      }
    ],
    investmentPerformance: [
      { month: 'Jan', value: 6000 },
      { month: 'Feb', value: 6200 },
      { month: 'Mar', value: 6150 },
      { month: 'Apr', value: 6400 },
      { month: 'May', value: 7000 },
      { month: 'Jun', value: 7475 },
    ]
  },
  aiInsights: {
    riskProfile: {
      riskTolerance: 'medium',
      investmentHorizon: 'long',
      investmentGoals: ['Retirement', 'Home Purchase'],
      preferredInvestments: ['Stocks', 'Bonds', 'Index Funds'],
      financialSituation: {
        emergencyFund: true,
        debtLevel: 'low',
        incomeStability: 'high'
      }
    },
    recommendations: [
      {
        id: "rec-1",
        category: "Savings",
        title: "Increase retirement contributions",
        description: "Consider increasing your 401(k) contributions to maximize employer match.",
        potentialSavings: 1200,
        priority: "high",
        implementationStatus: "pending"
      },
      {
        id: "rec-2",
        category: "Spending",
        title: "Reduce subscription services",
        description: "You have 5 streaming services totaling $85/month. Consider consolidating to 2-3 services.",
        potentialSavings: 420,
        priority: "medium",
        implementationStatus: "pending"
      }
    ],
    spendingInsights: [
      {
        id: "insight-1",
        category: "Dining",
        description: "Your dining expenses have increased compared to last month.",
        comparison: {
          current: 450,
          previous: 380,
          percentChange: 18.4
        }
      },
      {
        id: "insight-2",
        category: "Utilities",
        description: "Your utility expenses have decreased compared to last month.",
        comparison: {
          current: 180,
          previous: 220,
          percentChange: -18.2
        }
      }
    ],
    fraudAlerts: [
      {
        id: "fraud-1",
        date: "2023-06-15",
        description: "Unusual transaction at Electronics Store for $899.99",
        severity: "high",
        status: "resolved"
      }
    ]
  }
};

// Context type
interface UserDataContextType {
  userData: UserData;
  updateUserProfile: (profile: Partial<UserData['profile']>) => void;
  addTransaction: (transaction: Omit<Transaction, 'id'>) => void;
  updateTransaction: (id: string, transaction: Partial<Omit<Transaction, 'id'>>) => void;
  deleteTransaction: (id: string) => void;
  addBill: (bill: Omit<Bill, 'id'>) => void;
  updateBill: (id: string, bill: Partial<Omit<Bill, 'id'>>) => void;
  deleteBill: (id: string) => void;
  markBillAsPaid: (id: string, paid: boolean) => void;
  addAsset: (asset: Omit<Asset, 'id'>) => void;
  updateAsset: (id: string, asset: Partial<Omit<Asset, 'id'>>) => void;
  deleteAsset: (id: string) => void;
  addIncome: (income: Omit<Income, 'id'>) => void;
  updateIncome: (id: string, income: Partial<Omit<Income, 'id'>>) => void;
  deleteIncome: (id: string) => void;
  addSavingsGoal: (goal: Omit<SavingsGoal, 'id'>) => void;
  updateSavingsGoal: (id: string, goal: Partial<Omit<SavingsGoal, 'id'>>) => void;
  deleteSavingsGoal: (id: string) => void;
  updateSavingsGoalProgress: (id: string, amount: number) => void;
  updateRiskProfile: (profile: Partial<RiskProfile>) => void;
  updateFraudAlertStatus: (id: string, status: 'new' | 'reviewing' | 'resolved') => void;
  resetUserData: () => void;
}

// Create the context
const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

// ID generator utility
const generateId = () => Math.random().toString(36).substr(2, 9);

// Custom hook to use the context
export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

// Provider component
export const UserDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserData>(() => {
    // Try to get stored data from localStorage
    const storedData = localStorage.getItem('matrixFinanceUserData');
    return storedData ? JSON.parse(storedData) : defaultUserData;
  });

  // Save to localStorage whenever userData changes
  useEffect(() => {
    localStorage.setItem('matrixFinanceUserData', JSON.stringify(userData));
  }, [userData]);

  // Calculate totals after any data change
  useEffect(() => {
    const cashTotal = userData.finances.assets.items
      .filter(asset => asset.type === 'cash')
      .reduce((sum, asset) => sum + asset.value, 0);
    
    const investmentsTotal = userData.finances.assets.items
      .filter(asset => asset.type === 'investments')
      .reduce((sum, asset) => sum + asset.value, 0);
    
    const cryptoTotal = userData.finances.assets.items
      .filter(asset => asset.type === 'crypto')
      .reduce((sum, asset) => sum + asset.value, 0);
    
    const realEstateTotal = userData.finances.assets.items
      .filter(asset => asset.type === 'realEstate')
      .reduce((sum, asset) => sum + asset.value, 0);
    
    const otherTotal = userData.finances.assets.items
      .filter(asset => asset.type === 'other')
      .reduce((sum, asset) => sum + asset.value, 0);
    
    const assetsTotal = cashTotal + investmentsTotal + cryptoTotal + realEstateTotal + otherTotal;
    
    // Only update if values have changed to avoid infinite loops
    if (
      userData.finances.assets.total !== assetsTotal ||
      userData.finances.assets.breakdown.cash !== cashTotal ||
      userData.finances.assets.breakdown.investments !== investmentsTotal ||
      userData.finances.assets.breakdown.crypto !== cryptoTotal ||
      userData.finances.assets.breakdown.realEstate !== realEstateTotal ||
      userData.finances.assets.breakdown.other !== otherTotal
    ) {
      setUserData(prevData => ({
        ...prevData,
        finances: {
          ...prevData.finances,
          assets: {
            ...prevData.finances.assets,
            total: assetsTotal,
            breakdown: {
              cash: cashTotal,
              investments: investmentsTotal,
              crypto: cryptoTotal,
              realEstate: realEstateTotal,
              other: otherTotal
            }
          }
        }
      }));
    }
  }, [userData.finances.assets.items]);

  // Profile methods
  const updateUserProfile = (profile: Partial<UserData['profile']>) => {
    setUserData(prevData => ({
      ...prevData,
      profile: {
        ...prevData.profile,
        ...profile
      }
    }));
  };

  // Transaction methods
  const addTransaction = (transaction: Omit<Transaction, 'id'>) => {
    const newTransaction = { ...transaction, id: `tx-${generateId()}` };
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        transactions: [newTransaction, ...prevData.finances.transactions]
      }
    }));
  };

  const updateTransaction = (id: string, transaction: Partial<Omit<Transaction, 'id'>>) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        transactions: prevData.finances.transactions.map(tx => 
          tx.id === id ? { ...tx, ...transaction } : tx
        )
      }
    }));
  };

  const deleteTransaction = (id: string) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        transactions: prevData.finances.transactions.filter(tx => tx.id !== id)
      }
    }));
  };

  // Bill methods
  const addBill = (bill: Omit<Bill, 'id'>) => {
    const newBill = { ...bill, id: `bill-${generateId()}` };
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        bills: [...prevData.finances.bills, newBill]
      }
    }));
  };

  const updateBill = (id: string, bill: Partial<Omit<Bill, 'id'>>) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        bills: prevData.finances.bills.map(b => 
          b.id === id ? { ...b, ...bill } : b
        )
      }
    }));
  };

  const deleteBill = (id: string) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        bills: prevData.finances.bills.filter(b => b.id !== id)
      }
    }));
  };

  const markBillAsPaid = (id: string, paid: boolean) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        bills: prevData.finances.bills.map(b => 
          b.id === id ? { ...b, paid } : b
        )
      }
    }));
  };

  // Asset methods
  const addAsset = (asset: Omit<Asset, 'id'>) => {
    const newAsset = { ...asset, id: `asset-${generateId()}` };
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        assets: {
          ...prevData.finances.assets,
          items: [...prevData.finances.assets.items, newAsset]
        }
      }
    }));
  };

  const updateAsset = (id: string, asset: Partial<Omit<Asset, 'id'>>) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        assets: {
          ...prevData.finances.assets,
          items: prevData.finances.assets.items.map(a => 
            a.id === id ? { ...a, ...asset } : a
          )
        }
      }
    }));
  };

  const deleteAsset = (id: string) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        assets: {
          ...prevData.finances.assets,
          items: prevData.finances.assets.items.filter(a => a.id !== id)
        }
      }
    }));
  };

  // Income methods
  const addIncome = (income: Omit<Income, 'id'>) => {
    const newIncome = { ...income, id: `income-${generateId()}` };
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        income: {
          ...prevData.finances.income,
          sources: [...prevData.finances.income.sources, newIncome]
        }
      }
    }));

    // Recalculate monthly income total
    const updatedSources = [...userData.finances.income.sources, newIncome];
    const monthlyTotal = calculateTotalMonthlyIncome(updatedSources);
    
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        income: {
          monthly: monthlyTotal,
          sources: updatedSources
        }
      }
    }));
  };

  const updateIncome = (id: string, income: Partial<Omit<Income, 'id'>>) => {
    setUserData(prevData => {
      const updatedSources = prevData.finances.income.sources.map(src => 
        src.id === id ? { ...src, ...income } : src
      );
      
      const monthlyTotal = calculateTotalMonthlyIncome(updatedSources);
      
      return {
        ...prevData,
        finances: {
          ...prevData.finances,
          income: {
            monthly: monthlyTotal,
            sources: updatedSources
          }
        }
      };
    });
  };

  const deleteIncome = (id: string) => {
    setUserData(prevData => {
      const updatedSources = prevData.finances.income.sources.filter(src => src.id !== id);
      const monthlyTotal = calculateTotalMonthlyIncome(updatedSources);
      
      return {
        ...prevData,
        finances: {
          ...prevData.finances,
          income: {
            monthly: monthlyTotal,
            sources: updatedSources
          }
        }
      };
    });
  };

  // Calculate monthly income from various sources
  const calculateTotalMonthlyIncome = (sources: Income[]) => {
    return sources.reduce((total, source) => {
      let monthlyAmount = source.amount;
      switch (source.frequency) {
        case 'weekly':
          monthlyAmount = source.amount * 4.333; // Average weeks per month
          break;
        case 'biweekly':
          monthlyAmount = source.amount * 2.166; // Average biweekly periods per month
          break;
        case 'yearly':
          monthlyAmount = source.amount / 12;
          break;
        default: // 'monthly' is already correct
          break;
      }
      return total + monthlyAmount;
    }, 0);
  };

  // Savings Goal methods
  const addSavingsGoal = (goal: Omit<SavingsGoal, 'id'>) => {
    const newGoal = { ...goal, id: `goal-${generateId()}` };
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        savingsGoals: [...prevData.finances.savingsGoals, newGoal]
      }
    }));
  };

  const updateSavingsGoal = (id: string, goal: Partial<Omit<SavingsGoal, 'id'>>) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        savingsGoals: prevData.finances.savingsGoals.map(g => 
          g.id === id ? { ...g, ...goal } : g
        )
      }
    }));
  };

  const deleteSavingsGoal = (id: string) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        savingsGoals: prevData.finances.savingsGoals.filter(g => g.id !== id)
      }
    }));
  };

  const updateSavingsGoalProgress = (id: string, amount: number) => {
    setUserData(prevData => ({
      ...prevData,
      finances: {
        ...prevData.finances,
        savingsGoals: prevData.finances.savingsGoals.map(g => 
          g.id === id ? { ...g, currentAmount: amount } : g
        )
      }
    }));
  };

  // Risk Profile methods
  const updateRiskProfile = (profile: Partial<RiskProfile>) => {
    setUserData(prevData => ({
      ...prevData,
      aiInsights: {
        ...prevData.aiInsights,
        riskProfile: {
          ...prevData.aiInsights.riskProfile,
          ...profile
        }
      }
    }));
  };

  const updateFraudAlertStatus = (id: string, status: 'new' | 'reviewing' | 'resolved') => {
    setUserData(prevData => ({
      ...prevData,
      aiInsights: {
        ...prevData.aiInsights,
        fraudAlerts: prevData.aiInsights.fraudAlerts.map(alert => 
          alert.id === id ? { ...alert, status } : alert
        )
      }
    }));
  };

  // Reset data to defaults
  const resetUserData = () => {
    setUserData(defaultUserData);
  };

  return (
    <UserDataContext.Provider value={{
      userData,
      updateUserProfile,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      addBill,
      updateBill,
      deleteBill,
      markBillAsPaid,
      addAsset,
      updateAsset,
      deleteAsset,
      addIncome,
      updateIncome,
      deleteIncome,
      addSavingsGoal,
      updateSavingsGoal,
      deleteSavingsGoal,
      updateSavingsGoalProgress,
      updateRiskProfile,
      updateFraudAlertStatus,
      resetUserData
    }}>
      {children}
    </UserDataContext.Provider>
  );
};

export default UserDataProvider; 